import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Timer,
    ArrowsClockwise,
    SpeakerX,
    SpeakerSimpleHigh,
    MusicNote,
    CaretDown,
    CaretUp,
} from '@phosphor-icons/react';
import { SESSION, AUDIO } from '@/constants/testIds';
import { cue as fireCue, cueForTick } from '@/lib/audioCue';
import { useAudioPrefs } from '@/hooks/useAudioPrefs';

function format(seconds) {
    const s = Math.max(0, Math.floor(seconds));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

const MODES = [
    { id: 'off', label: 'Off', testId: AUDIO.modeOff, Icon: SpeakerX },
    { id: 'beeps', label: 'Beeps', testId: AUDIO.modeBeeps, Icon: MusicNote },
    { id: 'voice', label: 'Voice', testId: AUDIO.modeVoice, Icon: SpeakerSimpleHigh },
];

/**
 * Floating rest timer + audio cue dispatcher.
 *
 * Counts UP from `startedAt`. When `targetSeconds` is set, the display also
 * shows the remaining time and fires cues at 15/10/5/3/2/1/0s remaining (each
 * cue fires at most once per session of resting — tracked in a Set).
 *
 * `restartKey` should change whenever a new set is completed so the dedup Set
 * resets and the cues fire for the new rest period.
 */
export function RestTimer({ startedAt, targetSeconds = 90, restartKey, onReset }) {
    const [now, setNow] = useState(Date.now());
    const [mobileExpanded, setMobileExpanded] = useState(false);
    const prefs = useAudioPrefs();
    const firedRef = useRef(new Set());

    // Tick once a second while resting.
    useEffect(() => {
        if (!startedAt) return;
        setNow(Date.now()); // immediate first read so tick at remaining==target fires
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, [startedAt]);

    // Reset the de-dup Set whenever a new rest period begins.
    useEffect(() => {
        firedRef.current = new Set();
    }, [restartKey]);

    // Dispatch cues on tick. Effective target falls back to user's default
    // when the per-set value isn't supplied. Clamp elapsed >= 0 so the first
    // tick (remaining==target) reliably fires.
    const target = targetSeconds || prefs.defaultRestSeconds;
    useEffect(() => {
        if (!startedAt || !target) return;
        const elapsed = Math.max(0, Math.floor((now - startedAt) / 1000));
        const id = cueForTick(elapsed, target);
        if (id && !firedRef.current.has(id)) {
            firedRef.current.add(id);
            fireCue(id, { mode: prefs.mode, target });
        }
    }, [now, startedAt, target, prefs.mode]);

    const setMode = prefs.setMode;
    const setTarget = (v) => prefs.setDefaultRest(v);

    const elapsed = startedAt ? Math.max(0, (now - startedAt) / 1000) : 0;
    const remaining = Math.max(0, target - elapsed);
    const past = !!startedAt && elapsed >= target;

    const display = useMemo(() => {
        if (!startedAt) return null;
        return past ? `+${format(elapsed - target)}` : format(remaining);
    }, [startedAt, past, elapsed, remaining, target]);

    const isResting = !!startedAt;
    // On mobile, hide the toggle + rest-input behind a tap-to-expand chevron
    // when the user isn't actively resting. Saves real-estate on small screens
    // where the floating timer otherwise overlaps the set list. On desktop the
    // full bar is always shown (sm:flex utilities below).
    const showControlsMobile = isResting || mobileExpanded;

    return (
        <div
            data-testid={SESSION.restTimer}
            className="fixed bottom-5 left-5 z-30 select-none max-w-[calc(100vw-2.5rem)]"
        >
            <div
                className={`glass-card rounded-2xl pl-3 pr-2 py-2 flex items-center gap-2 sm:gap-3 sm:pl-4 sm:py-2.5 shadow-tactile ${
                    past ? 'border-[#00FF9D]/40 bg-[#00FF9D]/10' : ''
                }`}
            >
                <span
                    className={`grid place-items-center w-9 h-9 rounded-xl shrink-0 ${
                        past
                            ? 'bg-[#00FF9D] text-[#121212] animate-soft-pulse'
                            : 'bg-foreground/10 text-foreground'
                    }`}
                >
                    <Timer size={16} weight="bold" />
                </span>

                {startedAt ? (
                    <div className="leading-tight">
                        <p className="uppercase-mono text-muted-foreground">
                            Rest · {target}s
                        </p>
                        <p
                            data-testid={AUDIO.countdownDisplay}
                            className={`font-mono text-xl font-bold tabular-nums ${
                                past ? 'text-[#00FF9D]' : ''
                            }`}
                        >
                            {display}
                        </p>
                    </div>
                ) : (
                    <div className="leading-tight pr-1">
                        <p className="uppercase-mono text-muted-foreground">
                            Cue
                        </p>
                        <p className="font-mono text-sm font-bold capitalize">
                            {prefs.mode}
                        </p>
                    </div>
                )}

                {/* Mode toggle — 3-segment control */}
                <div
                    data-testid={AUDIO.modeToggle}
                    className={`${
                        showControlsMobile ? 'flex' : 'hidden'
                    } sm:flex items-center gap-1 p-1 rounded-xl border border-foreground/10 bg-foreground/5`}
                >
                    {MODES.map((m) => {
                        const active = prefs.mode === m.id;
                        return (
                            <button
                                key={m.id}
                                type="button"
                                onClick={() => setMode(m.id)}
                                data-testid={m.testId}
                                title={`${m.label} cues`}
                                aria-label={`${m.label} cues`}
                                aria-pressed={active}
                                className={`grid place-items-center w-7 h-7 rounded-lg transition-colors ${
                                    active
                                        ? 'bg-foreground text-background'
                                        : 'text-muted-foreground hover:text-foreground'
                                }`}
                            >
                                <m.Icon size={13} weight="bold" />
                            </button>
                        );
                    })}
                </div>

                {/* Compact rest-target input */}
                <input
                    data-testid={AUDIO.restTargetInput}
                    type="number"
                    inputMode="numeric"
                    min={10}
                    max={600}
                    step={5}
                    value={prefs.defaultRestSeconds}
                    onChange={(e) => setTarget(e.target.value)}
                    aria-label="Default rest seconds"
                    className={`${
                        showControlsMobile ? 'block' : 'hidden'
                    } sm:block w-12 font-mono text-xs font-bold tabular-nums text-center rounded-lg border border-foreground/10 bg-foreground/5 px-1 py-1 outline-none focus:border-foreground/40`}
                />

                {/* Mobile-only expand/collapse handle (hidden when resting) */}
                {!isResting && (
                    <button
                        type="button"
                        onClick={() => setMobileExpanded((v) => !v)}
                        data-testid="audio-mobile-expand"
                        aria-label={
                            mobileExpanded
                                ? 'Collapse audio controls'
                                : 'Expand audio controls'
                        }
                        aria-expanded={mobileExpanded}
                        className="sm:hidden grid place-items-center w-9 h-9 rounded-xl border border-foreground/10 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.96] transition-all"
                    >
                        {mobileExpanded ? (
                            <CaretDown size={14} weight="bold" />
                        ) : (
                            <CaretUp size={14} weight="bold" />
                        )}
                    </button>
                )}

                {startedAt && (
                    <button
                        type="button"
                        onClick={onReset}
                        data-testid={SESSION.restReset}
                        aria-label="Reset rest timer"
                        className="grid place-items-center w-9 h-9 rounded-xl border border-foreground/10 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.96] transition-all"
                    >
                        <ArrowsClockwise size={14} weight="bold" />
                    </button>
                )}
            </div>
        </div>
    );
}
